/**
 * MedCom Indbakke - Modtagne Beskeder
 */

import { useEffect, useState, type MouseEvent } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Mail, Download, ArrowLeft, Reply, Forward } from 'lucide-react';
import { CareCommunicationLetterDetails } from '@/pages/medcom/CareCommunicationLetterDetails';
import {
  acknowledgementLabel,
  duplicateImportFeedback,
  receiptStatusClass,
  receiptsForLetter,
  resendForOriginal,
  statusForLatestReceipt,
  type MessageResend,
} from '@/pages/medcom/acknowledgementDisplay';
import { useAcknowledgementOutcomeTexts } from '@/pages/medcom/useAcknowledgementOutcomes';
import {
  useCareCommunicationsList,
  useExportCareCommunication,
  useImportCareCommunication,
  useMessageAcknowledgements,
  useBundleJson,
  type CareCommunicationListFilters,
  type CareCommunicationListItem,
  type ImportCareCommunicationKind,
} from '@/hooks/useCareCommunications';
import { useActiveClinic } from '@/lib/activeClinic';
import { useAuth } from '@/lib/auth';
import { canImportReceiptManually } from '@/lib/referral/manualReceiptImport';
import { Banner } from '@/components/ui/Banner';
import { Button, ButtonLink } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { EntityTasksPanel } from '@/components/tasks/EntityTasksPanel';
import { EmptyState } from '@/components/ui/EmptyState';
import { Skeleton } from '@/components/ui/Skeleton';
import { ResponsiveTable, type TableColumn } from '@/components/ui/Table';
import { CareCommunicationPriorityChip } from '@/components/medcom/CareCommunicationPriorityChip';
import { OrganizationName } from '@/components/medcom/OrganizationName';
import { formatDistanceToNow } from 'date-fns';
import { da } from 'date-fns/locale';
import { useCareCommunicationReads } from '@/hooks/useCareCommunicationReads';
import { topicStandIn } from '@/lib/medcom/letterDisplay';
import { LetterCategory, LetterTitle, LetterTopic } from '@/pages/communicate/LetterColumns';
import { TYPE, TYPE_TABULAR } from '@/lib/typography';
import { cn } from '@/utils/cn';

export { acknowledgementLabel };

const OUTBOUND_LIST_FILTERS: CareCommunicationListFilters = {
  direction: 'out',
  status: 'all',
};

function inboxRowStatus(
  msg: CareCommunicationListItem,
  resend: MessageResend | null,
): { label: string; className: string } {
  if (msg.latestAcknowledgement) {
    const receipt = statusForLatestReceipt(
      msg.latestAcknowledgement.ackType,
      msg.latestAcknowledgement.createdAt,
      resend,
    );
    return { label: receipt.label, className: receiptStatusClass(receipt.tone) };
  }
  const className = msg.status === 'received'
    ? 'bg-ds-green-tint text-ds-green-ink'
    : msg.status === 'rejected' || msg.status === 'failed'
      ? 'bg-ds-red-tint text-ds-red-ink'
      : 'bg-ds-neutral-tint text-ds-text';
  return { label: inboxStatusLabel(msg.status), className };
}

export function inboxStatusLabel(status: CareCommunicationListItem['status']): string {
  switch (status) {
    case 'received':
      return 'Modtaget';
    case 'validated':
      return 'Valideret';
    case 'queued':
      return 'I kø';
    case 'sent':
      return 'Sendt';
    case 'delivered':
      return 'Leveret';
    case 'acknowledged':
      return 'Kvitteret';
    case 'failed':
      return 'Fejlet';
    case 'rejected':
      return 'Afvist';
    default: {
      const unexpected: never = status;
      return unexpected;
    }
  }
}

function receiveOverviewActivityLabel(
  code: string | null | undefined,
): string | null {
  if (code === 'new-message') return 'Ny besked';
  if (code === 'reply-message') return 'Svar';
  if (code === 'forward-message') return 'Videresend';
  return null;
}

function importKindMessage(kind: ImportCareCommunicationKind, resent = false): string {
  switch (kind) {
    case 'care-communication':
      return 'Meddelelsen er indlæst.';
    case 'duplicate':
      return duplicateImportFeedback(resent);
    case 'acknowledgement':
      return 'Kvitteringen er indlæst.';
    case 'receipt':
      return 'Kvitteringen er indlæst.';
    case 'unpack-error':
      return 'Filen kunne ikke indlæses.';
    default: {
      const unexpected: never = kind;
      return unexpected;
    }
  }
}

function thrownImportMessage(err: unknown): string {
  if (err instanceof Error && err.message.length > 0) return err.message;
  return 'Filen kunne ikke indlæses.';
}

function ImportCareCommunicationControl({ clinicId }: { clinicId: string | null }) {
  const { isPlatformAdmin, isClinicAdmin } = useAuth();
  const importMutation = useImportCareCommunication();
  const [file, setFile] = useState<File | null>(null);
  const [feedback, setFeedback] = useState<{ ok: boolean; text: string } | null>(null);
  const [patientLink, setPatientLink] = useState<{
    id: string;
    created: boolean;
    name: string;
  } | null>(null);
  const [patientLinkFailed, setPatientLinkFailed] = useState(false);

  const onImport = async () => {
    if (!clinicId || !file || importMutation.isPending) return;
    setFeedback(null);
    setPatientLink(null);
    setPatientLinkFailed(false);
    try {
      const payload = await file.text();
      const result = await importMutation.mutateAsync({ clinicId, payload });
      setFeedback({
        ok: true,
        text: importKindMessage(result.kind, result.acknowledgementResent === true),
      });
      if (result.patientId && result.patientName) {
        setPatientLink({
          id: result.patientId,
          created: result.patientCreated === true,
          name: result.patientName,
        });
      } else if (result.patientLinkFailed) {
        setPatientLinkFailed(true);
      }
      setFile(null);
    } catch (err) {
      setFeedback({ ok: false, text: thrownImportMessage(err) });
    }
  };

  // Manual import is admin-only; communicate-cc-import enforces the same rule (403).
  if (!canImportReceiptManually({ isPlatformAdmin, isClinicAdmin: clinicId ? isClinicAdmin(clinicId) : false })) {
    return null;
  }

  return (
    <Card className="mb-s4">
      <label className={cn(TYPE.label, 'block text-ds-text')} htmlFor="medcom-cc-import">
        Indlæs meddelelse (kun admin)
      </label>
      <p className={cn(TYPE.body, 'mt-s1 text-ds-muted')}>
        Vælg en brevfil (JSON eller XML).
      </p>
      <div className="mt-s3 flex flex-wrap items-center gap-s3">
        <input
          id="medcom-cc-import"
          type="file"
          accept=".json,.xml,application/json,application/fhir+json,application/fhir+xml,text/xml"
          className="text-sm"
          onChange={(event) => {
            const picked = event.target.files?.[0] ?? null;
            event.target.value = '';
            setFile(picked);
            setFeedback(null);
            setPatientLink(null);
            setPatientLinkFailed(false);
          }}
        />
        <Button
          type="button"
          onClick={() => void onImport()}
          disabled={importMutation.isPending || !file || !clinicId}
        >
          {importMutation.isPending ? 'Indlæser…' : 'Indlæs'}
        </Button>
      </div>
      {file && !feedback && (
        <p className="mt-2 text-sm text-ds-muted">Fil valgt.</p>
      )}
      {feedback && (
        <p className={feedback.ok ? 'mt-2 text-sm text-ds-green-ink' : 'mt-2 text-sm text-ds-red-ink'}>
          {feedback.text}
        </p>
      )}
      {patientLinkFailed && (
        <p className="mt-1 text-sm text-ds-red-ink">Patienten kunne ikke knyttes.</p>
      )}
      {patientLink && (
        <p className="mt-1 text-sm text-ds-green-ink">
          {patientLink.created
            ? `Patienten ${patientLink.name} er oprettet. `
            : `Beskeden er knyttet til ${patientLink.name}. `}
          <Link
            to={`/patients/${patientLink.id}?tab=medcom`}
            className="underline"
          >
            Åbn patienten
          </Link>
        </p>
      )}
    </Card>
  );
}

interface Props {
  clinicId: string | null;
  onReply?: (communicationIdentifier: string, messageId: string) => void;
}

function forwardLetterTo(communicationIdentifier: string, messageId: string): string {
  return `/medcom/test?action=forward&threadId=${encodeURIComponent(communicationIdentifier)}&messageId=${encodeURIComponent(messageId)}`;
}

export function MedComInbox({ clinicId, onReply }: Props) {
  const { t } = useTranslation();
  const exportMutation = useExportCareCommunication();

  const [filters] = useState<CareCommunicationListFilters>({
    direction: 'in',
    status: 'all',
  });

  const [selectedIdentifier, setSelectedIdentifier] = useState<string | null>(null);

  const { data, isLoading, error, refetch } = useCareCommunicationsList(clinicId, filters);
  const { data: outbound } = useCareCommunicationsList(clinicId, OUTBOUND_LIST_FILTERS);
  const outboundItems = outbound?.items ?? [];
  const { isUnread, markRead } = useCareCommunicationReads(clinicId, data?.items);
  const messages = data?.items ?? [];
  const opened = selectedIdentifier
    ? messages.find((message) => message.communicationIdentifier === selectedIdentifier)
    : undefined;
  const openedId = opened?.id;
  const openedCommunicationId = opened?.communicationIdentifier;

  useEffect(() => {
    if (openedId) markRead(openedId);
    if (openedCommunicationId) markRead(openedCommunicationId);
  }, [openedId, openedCommunicationId, markRead]);

  const handleExport = async (
    messageId: string,
    format: 'json' | 'xml',
    acknowledgementId?: string,
  ) => {
    if (!clinicId) return;
    try {
      await exportMutation.mutateAsync({
        clinicId,
        messageId,
        format,
        acknowledgementId,
      });
    } catch (err) {
      console.error('Eksport fejlede:', err);
    }
  };

  const openLetter = (msg: CareCommunicationListItem) => {
    markRead(msg.id);
    markRead(msg.communicationIdentifier);
    setSelectedIdentifier(msg.communicationIdentifier);
  };

  if (selectedIdentifier) {
    const msg = messages.find((m) => m.communicationIdentifier === selectedIdentifier);
    if (msg) {
      return (
        <MedComThreadDetail
          msg={msg}
          resend={resendForOriginal(msg.id, msg.resend, outboundItems)}
          onBack={() => setSelectedIdentifier(null)}
          onReply={onReply}
          onExport={handleExport}
          exporting={exportMutation.isPending}
        />
      );
    }
  }

  const columns: TableColumn<CareCommunicationListItem>[] = [
    {
      id: 'status',
      header: t('communicate.col_status'),
      cell: (msg) => (
        <InboxStatusCell
          msg={msg}
          unread={isUnread(msg)}
          resend={resendForOriginal(msg.id, msg.resend, outboundItems)}
        />
      ),
    },
    {
      id: 'topic',
      header: t('communicate.col_topic'),
      cell: (msg) => (
        <span className={cn(TYPE.body, isUnread(msg) && 'font-medium', 'text-ds-text')}>
          <LetterTopic topic={msg.topic} category={msg.category} />
        </span>
      ),
    },
    {
      id: 'category',
      header: t('communicate.col_category'),
      cell: (msg) => <LetterCategory category={msg.category} />,
      hideOnMobile: true,
    },
    {
      id: 'sender',
      header: t('communicate.col_sender'),
      cell: (msg) => (
        <OrganizationName name={msg.senderOrganization} sorId={msg.senderSor} fallback="—" />
      ),
    },
    {
      id: 'patient',
      header: t('communicate.col_patient'),
      cell: (msg) => msg.patientName?.trim() || '—',
    },
    {
      id: 'type',
      header: t('communicate.col_type'),
      cell: (msg) => receiveOverviewActivityLabel(msg.activityCode) ?? '—',
      hideOnMobile: true,
    },
    {
      id: 'date',
      header: t('communicate.col_date'),
      cell: (msg) => (
        <span className={TYPE_TABULAR}>
          {msg.receivedAt
            ? formatDistanceToNow(new Date(msg.receivedAt), { addSuffix: true, locale: da })
            : '—'}
        </span>
      ),
    },
    {
      id: 'row-actions',
      header: <span className="sr-only">{t('communicate.reply')}</span>,
      cell: (msg) => (
        <InboxRowActions
          msg={msg}
          exporting={exportMutation.isPending}
          onReply={onReply}
          onExport={handleExport}
        />
      ),
    },
  ];

  return (
    <div>
      <ImportCareCommunicationControl clinicId={clinicId} />

      {error ? (
        <div className="mb-s4">
          <Banner
            tone="error"
            action={
              <Button type="button" variant="secondary" size="sm" onClick={() => void refetch()}>
                {t('app.retry')}
              </Button>
            }
          >
            {t('communicate.load_error')}
          </Banner>
        </div>
      ) : null}

      {isLoading ? (
        <Card>
          <div className="flex flex-col gap-s3" aria-busy>
            {Array.from({ length: 6 }, (_, index) => (
              <Skeleton key={index} className="h-10 w-full" />
            ))}
          </div>
        </Card>
      ) : !data || messages.length === 0 ? (
        <Card>
          <EmptyState icon={<Mail className="h-7 w-7" />} title={t('medcomSection.empty_inbox')} />
        </Card>
      ) : (
        <Card className="p-0 nav:p-0">
          <ResponsiveTable
            columns={columns}
            rows={messages}
            getRowKey={(msg) => msg.id}
            onRowClick={openLetter}
            getRowLabel={(msg) => {
              const categoryName = msg.category?.trim()
                ? t(`communicate.category_${msg.category}`, { defaultValue: msg.category })
                : null;
              return (
                topicStandIn(msg.topic, categoryName)?.text ||
                msg.patientName?.trim() ||
                msg.communicationIdentifier
              );
            }}
            empty={null}
            mobileCard={(msg) => (
              <div className="w-full text-left">
                <div className={cn(TYPE.body, 'font-medium text-ds-text')}>
                  <LetterTopic topic={msg.topic} category={msg.category} />
                </div>
                <div className={cn(TYPE.small, 'mt-s1 text-ds-muted')}>
                  <OrganizationName
                    name={msg.senderOrganization}
                    sorId={msg.senderSor}
                    fallback="—"
                  />
                  {' · '}
                  {msg.patientName?.trim() || '—'}
                </div>
                <div className="mt-s2 flex flex-wrap items-center gap-s2">
                  <InboxStatusCell
                    msg={msg}
                    unread={isUnread(msg)}
                    resend={resendForOriginal(msg.id, msg.resend, outboundItems)}
                  />
                </div>
              </div>
            )}
          />
        </Card>
      )}
    </div>
  );
}

function InboxStatusCell({
  msg,
  unread,
  resend,
}: {
  msg: CareCommunicationListItem;
  unread: boolean;
  resend: MessageResend | null;
}) {
  const { t } = useTranslation();
  const rowStatus = inboxRowStatus(msg, resend);
  return (
    <span className="inline-flex flex-wrap items-center gap-s2">
      {unread ? (
        <span className="inline-flex items-center gap-s1 rounded bg-ds-coral-badge px-s2 py-s1 text-ds-on-danger">
          <span className="h-1.5 w-1.5 rounded-full bg-ds-on-danger" aria-hidden />
          {t('messages.inbox.unread_indicator')}
        </span>
      ) : null}
      <span className={`rounded px-s2 py-s1 ${rowStatus.className}`}>{rowStatus.label}</span>
      {msg.latestAcknowledgement ? (
        <span
          className={`rounded px-s2 py-s1 ${
            msg.latestAcknowledgement.ackType === 'AA'
              ? 'bg-ds-green-tint text-ds-green-ink'
              : msg.latestAcknowledgement.ackType === 'AE'
                ? 'bg-ds-red-tint text-ds-red-ink'
                : 'bg-ds-amber-tint text-ds-amber-ink'
          }`}
        >
          Kvittering: {acknowledgementLabel(msg.latestAcknowledgement.ackType)}
        </span>
      ) : null}
      <CareCommunicationPriorityChip priority={msg.priority} variant="compact" />
    </span>
  );
}

function InboxRowActions({
  msg,
  exporting,
  onReply,
  onExport,
}: {
  msg: CareCommunicationListItem;
  exporting: boolean;
  onReply?: (id: string, messageId: string) => void;
  onExport: (messageId: string, format: 'json' | 'xml') => void;
}) {
  const { t } = useTranslation();
  return (
    <div className="flex items-center gap-s1">
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className="h-8 w-8 p-0"
        aria-label={t('communicate.reply')}
        onClick={(event) => {
          event.stopPropagation();
          onReply?.(msg.communicationIdentifier, msg.id);
        }}
      >
        <Reply className="h-4 w-4" />
      </Button>
      <ButtonLink
        variant="ghost"
        size="sm"
        className="h-8 w-8 p-0"
        aria-label={t('communicate.forward')}
        to={forwardLetterTo(msg.communicationIdentifier, msg.id)}
        onClick={(event: MouseEvent) => event.stopPropagation()}
      >
        <Forward className="h-4 w-4" />
      </ButtonLink>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        aria-label={t('communicate.export_json')}
        disabled={exporting}
        onClick={(event) => {
          event.stopPropagation();
          onExport(msg.id, 'json');
        }}
      >
        JSON
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        aria-label={t('communicate.export_xml')}
        disabled={exporting}
        onClick={(event) => {
          event.stopPropagation();
          onExport(msg.id, 'xml');
        }}
      >
        XML
      </Button>
    </div>
  );
}

// ── Inline thread detail (no extra API call — uses list data + export) ────────

interface DetailProps {
  msg: CareCommunicationListItem;
  resend: MessageResend | null;
  onBack: () => void;
  onReply?: (id: string, messageId: string) => void;
  onExport: (
    messageId: string,
    format: 'json' | 'xml',
    acknowledgementId?: string,
  ) => void;
  exporting: boolean;
}

function MedComThreadDetail({
  msg,
  resend,
  onBack,
  onReply,
  onExport,
  exporting,
}: DetailProps) {
  const { t } = useTranslation();
  const { communicationIdentifier } = msg;
  const { activeClinicId } = useActiveClinic();
  const { data: bundle, isLoading: bundleLoading } = useBundleJson(activeClinicId, msg.id);
  const { data: storedAcks } = useMessageAcknowledgements(activeClinicId, msg.id);
  const ackSources = storedAcks?.acknowledgements ?? (
    msg.latestAcknowledgement
      ? [{
          ...msg.latestAcknowledgement,
          duplicateResend: msg.acknowledgementCount > 1,
        }]
      : []
  );
  const { data: outcomeTexts } = useAcknowledgementOutcomeTexts(
    activeClinicId,
    msg.id,
    ackSources,
  );
  const receipts = receiptsForLetter(ackSources, outcomeTexts, resend);
  const acknowledgementId = receipts.cards[0]?.id ?? msg.latestAcknowledgement?.id;
  const statusLabel = receipts.status?.label ?? inboxStatusLabel(msg.status);

  const categoryCode = bundle?.categoryCode ?? msg.category ?? null;
  const topic = (bundle?.topic ?? msg.topic)?.trim() || null;
  const activityText = bundle?.activityCode
    ? t(`communicate.activity_${bundle.activityCode}`, {
        defaultValue: bundle.activityCode,
      })
    : null;
  const priorityBadge = (
    <CareCommunicationPriorityChip
      priority={bundle?.priority ?? msg.priority}
      variant="compact"
    />
  );

  return (
    <div>
      <button
        type="button"
        onClick={onBack}
        className="mb-4 inline-flex items-center text-sm text-ds-muted hover:text-ds-text"
      >
        <ArrowLeft className="mr-1 h-4 w-4" />
        Tilbage til indbakke
      </button>

      <div className="flex items-start justify-between mb-6">
        <div>
          <LetterTitle
            category={categoryCode}
            topic={topic}
            fallback={t('communicate.thread_title')}
          />
          {activityText || bundle?.priority || msg.priority ? (
            <div className="mt-2 flex flex-wrap items-center gap-s2 text-sm text-ds-muted">
              {activityText ? <span className="text-ds-muted">{activityText}</span> : null}
              {priorityBadge}
            </div>
          ) : null}
        </div>

        <div className="flex flex-wrap gap-2">
          <Button variant="secondary" onClick={() => onReply?.(communicationIdentifier, msg.id)}>
            <Reply className="mr-1 h-4 w-4" />
            Besvar
          </Button>
          <ButtonLink variant="secondary" to={forwardLetterTo(communicationIdentifier, msg.id)}>
            <Forward className="mr-1 h-4 w-4" />
            Videresend
          </ButtonLink>
          <Button variant="secondary" size="sm" disabled={exporting} onClick={() => onExport(msg.id, 'json')}>
            <Download className="mr-1 h-3.5 w-3.5" />JSON
          </Button>
          <Button variant="secondary" size="sm" disabled={exporting} onClick={() => onExport(msg.id, 'xml')}>
            <Download className="mr-1 h-3.5 w-3.5" />XML
          </Button>
          {acknowledgementId && (
            <>
              <Button
                variant="secondary"
                size="sm"
                disabled={exporting}
                onClick={() => onExport(msg.id, 'json', acknowledgementId)}
              >
                <Download className="mr-1 h-3.5 w-3.5" />Kvittering JSON
              </Button>
              <Button
                variant="secondary"
                size="sm"
                disabled={exporting}
                onClick={() => onExport(msg.id, 'xml', acknowledgementId)}
              >
                <Download className="mr-1 h-3.5 w-3.5" />Kvittering XML
              </Button>
            </>
          )}
        </div>
      </div>

      {bundleLoading && (
        <div className="text-center py-8 text-ds-muted text-sm">Henter besked indhold...</div>
      )}

      {!bundleLoading && bundle && (
        <CareCommunicationLetterDetails
          bundle={bundle}
          receivedAt={msg.receivedAt}
          status={msg.status}
          statusLabel={statusLabel}
          statusTone={receipts.status?.tone}
          acknowledgements={receipts.cards}
          ackTimeoutWarning={
            msg.ackTimeoutExhausted ? t('communicate.ack_timeout_exhausted') : null
          }
          onExportAcknowledgement={(ackId, format) => onExport(msg.id, format, ackId)}
          onExportMessage={(messageId, format) => onExport(messageId, format)}
        />
      )}

      {activeClinicId && communicationIdentifier ? (
        <div className="mt-s5">
          <EntityTasksPanel
            clinicId={activeClinicId}
            entityType="letter"
            entityId={communicationIdentifier}
          />
        </div>
      ) : null}

    </div>
  );
}
